import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Loader2, Send, X, Paperclip, FileText, ShieldCheck, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import AttachFromVaultDialog from "./AttachFromVaultDialog";

/**
 * Compose / Reply dialog. Driven by `initial`:
 *   - `null` / `{}` — fresh email
 *   - `{ replyTo: { from, subject, message_id, references, html, plain, date } }`
 *     — pre-fills To, prefixes "Re:" on subject, quotes the original below the
 *     cursor, and includes In-Reply-To + References for proper threading.
 *
 * On successful send, calls `onSent()` so the parent can refresh the Sent
 * box and close back to the inbox detail view.
 */
export default function ComposeEmailDialog({ open, initial, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showAttach, setShowAttach] = useState(false);

  useEffect(() => {
    if (!open) return;
    const reply = initial?.replyTo;
    if (reply) {
      setTo(extractEmail(reply.from));
      setSubject(prefixRe(reply.subject || ""));
      setBody(buildReplyDraft(reply));
    } else {
      setTo("");
      setSubject("");
      setBody("");
    }
    setCc("");
    setShowCc(false);
    setAttachments([]);
    setShowAttach(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.replyTo?.message_id]);

  const send = async () => {
    const toList = parseAddresses(to);
    if (toList.length === 0) { toast.error("At least one recipient required"); return; }
    if (!subject.trim()) { toast.error("Subject required"); return; }
    setSending(true);
    try {
      const ccList = parseAddresses(cc);
      const reply = initial?.replyTo;
      const payload = {
        to: toList,
        cc: ccList.length ? ccList : undefined,
        subject: subject.trim(),
        html: textToHtml(body),
      };
      if (reply?.message_id) payload.in_reply_to = reply.message_id;
      if (reply?.references) {
        // Preserve the chain and append the message we're replying to
        const chain = `${reply.references} ${reply.message_id || ""}`.trim();
        payload.references = chain;
      } else if (reply?.message_id) {
        payload.references = reply.message_id;
      }
      if (attachments.length > 0) payload.attachments = attachments;
      const r = await api.post("/mail/compose", payload);
      if (r.data?.ok) {
        const skipped = r.data.skipped_attachments || 0;
        const attached = r.data.attached || 0;
        if (skipped > 0) {
          toast.warning(`Sent to ${toList.join(", ")} — ${attached} attached, ${skipped} could not be resolved`);
        } else {
          toast.success(`Email sent to ${toList.join(", ")}${attached ? ` with ${attached} attachment${attached === 1 ? "" : "s"}` : ""}`);
        }
        onSent?.();
        onClose();
      } else {
        toast.error(r.data?.error || "Send failed");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="compose-email-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>
            {initial?.replyTo ? "Reply" : "New email"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-y-auto">
          <Field label="To" required>
            <input
              type="text"
              className="input-clean w-full"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="someone@example.com, another@example.com"
              autoComplete="off"
              data-testid="compose-to"
            />
          </Field>
          {showCc ? (
            <Field label="Cc">
              <input
                type="text"
                className="input-clean w-full"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="cc@example.com"
                autoComplete="off"
                data-testid="compose-cc"
              />
            </Field>
          ) : (
            <button
              type="button"
              onClick={() => setShowCc(true)}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
              data-testid="compose-add-cc"
            >
              + Add CC
            </button>
          )}
          <Field label="Subject" required>
            <input
              type="text"
              className="input-clean w-full"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              data-testid="compose-subject"
            />
          </Field>
          <Field label="Message">
            <textarea
              rows={10}
              className="input-clean w-full font-sans"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={50000}
              placeholder="Write your message…"
              data-testid="compose-body"
            />
          </Field>
          {attachments.length > 0 ? (
            <div className="space-y-1.5" data-testid="compose-attachments-list">
              <div className="text-[11px] uppercase tracking-wider text-slate-600" style={{ fontFamily: "Outfit" }}>
                Attachments ({attachments.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {attachments.map((ref, idx) => (
                  <AttachmentChip
                    key={`${ref.kind}-${ref.lease_id || ref.envelope_id || ref.snapshot_id || idx}`}
                    ref_={ref}
                    onRemove={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter className="flex-shrink-0 pt-3">
          <button
            type="button"
            onClick={() => setShowAttach(true)}
            disabled={sending}
            className="btn-outline-dark inline-flex items-center gap-1 mr-auto"
            data-testid="compose-attach-from-vault"
          >
            <Paperclip className="w-3 h-3" /> Attach from vault
            {attachments.length > 0 ? (
              <span className="text-[10px] bg-slate-900 text-white px-1.5 rounded-sm ml-1">{attachments.length}</span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="btn-outline-dark inline-flex items-center gap-1"
            data-testid="compose-cancel"
          >
            <X className="w-3 h-3" /> Cancel
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending}
            className="btn-primary-dark inline-flex items-center gap-2"
            data-testid="compose-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : "Send"}
          </button>
        </DialogFooter>
      </DialogContent>
      <AttachFromVaultDialog
        open={showAttach}
        currentSelection={attachments}
        onClose={() => setShowAttach(false)}
        onSelect={(refs) => setAttachments(refs)}
      />
    </Dialog>
  );
}

function AttachmentChip({ ref_, onRemove }) {
  const meta = chipMeta(ref_);
  const Icon = meta.icon;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs ${meta.bg}`}
      data-testid={`compose-attachment-chip-${ref_.kind}`}
    >
      <Icon className={`w-3 h-3 ${meta.iconClass}`} />
      <span className="text-slate-800 font-medium">{meta.label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-slate-400 hover:text-rose-600 ml-0.5"
        aria-label="Remove attachment"
        data-testid={`compose-attachment-remove-${ref_.kind}`}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function chipMeta(ref) {
  switch (ref.kind) {
    case "lease_signed":
      return { icon: ShieldCheck, label: "Signed lease PDF", bg: "bg-emerald-50 border-emerald-200", iconClass: "text-emerald-700" };
    case "lease_binder":
      return { icon: BookOpen, label: "Year-end binder", bg: "bg-sky-50 border-sky-200", iconClass: "text-sky-700" };
    case "lease_custom_pdf":
      return { icon: FileText, label: "Custom lease PDF", bg: "bg-violet-50 border-violet-200", iconClass: "text-violet-700" };
    case "lease_draft":
    default:
      return { icon: FileText, label: "Lease draft", bg: "bg-slate-50 border-slate-200", iconClass: "text-slate-600" };
  }
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-slate-600 mb-1 block" style={{ fontFamily: "Outfit" }}>
        {label}{required ? <span className="text-rose-600">*</span> : null}
      </span>
      {children}
    </label>
  );
}

// --- Parsing / formatting helpers -----------------------------------------

function parseAddresses(raw) {
  return (raw || "")
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter((s) => s && s.includes("@"));
}

function extractEmail(fromHeader) {
  if (!fromHeader) return "";
  // "Name <email@host>" → email@host; "email@host" → email@host
  const m = fromHeader.match(/<([^>]+)>/);
  return (m ? m[1] : fromHeader).trim();
}

function prefixRe(subject) {
  const trimmed = (subject || "").trim();
  if (/^re:/i.test(trimmed)) return trimmed;
  return `Re: ${trimmed}`;
}

function buildReplyDraft(reply) {
  const date = reply.date || "earlier";
  const sender = reply.from || "the sender";
  const original = (reply.plain || stripHtml(reply.html) || "").trim();
  const quoted = original.split("\n").map((l) => `> ${l}`).join("\n");
  return `\n\n---\nOn ${date}, ${sender} wrote:\n${quoted}\n`;
}

function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<style.*?<\/style>/gis, " ")
    .replace(/<script.*?<\/script>/gis, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n\s*\n+/g, "\n\n")
    .trim();
}

function textToHtml(text) {
  // Very basic — preserve paragraphs and quoted-lines on > prefix
  const escaped = (text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<div style="font-family:Helvetica,Arial,sans-serif;font-size:14px;color:#0f172a;line-height:1.55;white-space:pre-wrap;">${escaped}</div>`;
}
